import { useMemo } from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@occc-app/design-system';
import { ActivateTransactionViewModel } from '@occc-app/dataAccessLayer/activateTransaction/queries';

import { TransactionViewSavingItem } from './TransactionViewSavingItem';
import { getPointChangesSaving } from './utils';

type TransactionViewPointsSummaryProps = {
  totalPointsEarned: number;
  totalPointsSpent: number;
  pointTransactions: ActivateTransactionViewModel['pointTransactions'] | null;
};

export const TransactionViewPointsSummary = (props: TransactionViewPointsSummaryProps) => {
  const { totalPointsEarned, totalPointsSpent, pointTransactions } = props;

  const savings = useMemo(() => getPointChangesSaving(pointTransactions), [pointTransactions]);

  if (totalPointsEarned === 0 && totalPointsSpent === 0 && !savings.length) {
    return null;
  }

  // Always show earned; add a spent row only when points were actually spent so the
  // summary reflects the guard above (which renders for spend-only transactions too).
  const summaryRows = [
    { label: 'Total Points Earned', value: totalPointsEarned },
    ...(totalPointsSpent !== 0 ? [{ label: 'Total Points Spent', value: totalPointsSpent }] : []),
  ];

  return (
    <TableContainer
      sx={(theme) => ({
        marginBottom: theme.spacing(1),
      })}
    >
      <Table>
        <TableBody>
          {summaryRows.map((row, index) => {
            // The last visible summary row drops its bottom border only when no savings
            // rows follow it; interior rows keep the divider between summary lines.
            const borderBottom =
              index === summaryRows.length - 1 && !savings.length ? 'none' : undefined;
            return (
              <TableRow key={row.label}>
                <TableCell sx={{ borderBottom }}>{row.label}</TableCell>
                <TableCell align='right' sx={{ borderBottom }}>
                  {row.value}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {savings.length > 0 ? (
        <Box sx={{ px: 2, pb: 1 }}>
          <TransactionViewSavingItem savings={savings} />
        </Box>
      ) : null}
    </TableContainer>
  );
};
